/**
 * In-memory printer (tests, dev, and "no printer yet"): keeps every job so tests / the Playwright
 * print-queue scenario can check what would have been printed, and can be told to fail
 * (`unplug()` = the cable was pulled mid-sale, `plug()` = back).
 */
import { DRAWER_KICK } from "./escpos";
import {
  PrinterError,
  type PrinterErrorCode,
  type PrinterDriver,
  type PrinterStatus,
} from "./types";

export interface MockJob {
  bytes: Uint8Array;
  at: number;
}

export class MockPrinter implements PrinterDriver {
  readonly kind = "mock" as const;
  readonly jobs: MockJob[] = [];
  drawerKicks = 0;
  private failure: PrinterErrorCode | null = null;
  private failTimes = 0;

  supported(): boolean {
    return true;
  }

  /** Every print fails (`disconnected`) until `plug()`. */
  unplug(code: PrinterErrorCode = "disconnected"): void {
    this.failure = code;
    this.failTimes = Number.POSITIVE_INFINITY;
  }

  /** The next `n` prints fail, then it works again. */
  failNext(n = 1, code: PrinterErrorCode = "write_failed"): void {
    this.failure = code;
    this.failTimes = n;
  }

  plug(): void {
    this.failure = null;
    this.failTimes = 0;
  }

  private check(): void {
    if (this.failure && this.failTimes > 0) {
      this.failTimes -= 1;
      const code = this.failure;
      if (this.failTimes === 0) {
        this.failure = null;
      }
      throw new PrinterError(code);
    }
  }

  async connect(): Promise<PrinterStatus> {
    return this.status();
  }

  async print(bytes: Uint8Array): Promise<void> {
    this.check();
    this.jobs.push({ bytes: bytes.slice(), at: Date.now() });
  }

  async openDrawer(): Promise<void> {
    this.check();
    this.drawerKicks += 1;
    this.jobs.push({ bytes: DRAWER_KICK.slice(), at: Date.now() });
  }

  async status(): Promise<PrinterStatus> {
    const broken = this.failure !== null && this.failTimes > 0;
    return { connected: !broken, name: "Test printer", error: broken ? this.failure : null };
  }

  async disconnect(): Promise<void> {
    // nothing
  }
}
